import { useEffect, useRef } from "react";

interface TurnstileCAPTCHAProps {
  onVerify: (token: string) => void;
  onError?: (error: string) => void;
}

/**
 * Cloudflare Turnstile CAPTCHA component
 * Prevents bot attacks on signup/login endpoints
 */
export function TurnstileCAPTCHA({ onVerify, onError }: TurnstileCAPTCHAProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const widgetId = useRef<string | null>(null);

  useEffect(() => {
    // Load Turnstile script
    if (!document.getElementById('turnstile-script')) {
      const script = document.createElement('script');
      script.id = 'turnstile-script';
      script.src = 'https://challenges.cloudflare.com/turnstile/v0/api.js';
      script.async = true;
      script.defer = true;
      document.head.appendChild(script);
    }

    const initTurnstile = () => {
      if (containerRef.current && (window as any).turnstile && !widgetId.current) {
        try {
          widgetId.current = (window as any).turnstile.render(containerRef.current, {
            sitekey: import.meta.env.VITE_TURNSTILE_SITE_KEY || '1x00000000000000000000AA', // Test key
            callback: (token: string) => {
              console.log('CAPTCHA verified');
              onVerify(token);
            },
            'error-callback': () => {
              const error = 'CAPTCHA verification failed';
              console.error(error);
              onError?.(error);
            },
            theme: 'light',
            size: 'normal',
          });
        } catch (error) {
          console.error('Failed to initialize Turnstile:', error);
          onError?.('Failed to load CAPTCHA');
        }
      }
    };

    if ((window as any).turnstile) {
      initTurnstile();
    } else {
      const checkInterval = setInterval(() => {
        if ((window as any).turnstile) {
          clearInterval(checkInterval);
          initTurnstile();
        }
      }, 100);

      return () => clearInterval(checkInterval);
    }

    return () => {
      if (widgetId.current && (window as any).turnstile) {
        try {
          (window as any).turnstile.remove(widgetId.current);
        } catch (error) {
          console.error('Failed to remove Turnstile widget:', error);
        }
      }
    };
  }, [onVerify, onError]);

  return (
    <div className="flex justify-center my-4">
      <div ref={containerRef} />
    </div>
  );
}
